import {Injectable} from '@angular/core'
import {NavigationExtras, Router} from "@angular/router"
import {BehaviorSubject, Observable, of} from "rxjs"
import {LoginRequestModel, UserDetailModel} from "../../../domain/src/lib/user.model"
import {Redirects, RoleType} from "../../../domain/src/lib/authorization.model"
import {StorageService} from "./storage.service"
import {UserService} from "./user.service"


@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  // TODO: browser refresh should not logout user
  private readonly AUTH_KEY = 'auth_user';
  isLoading = false
  user!: UserDetailModel
  roleStrings!: string[]
  private readonly isUserLoggedIn = new BehaviorSubject<boolean>(false)
  private readonly userRoles = new BehaviorSubject<string[]>([])
  private readonly rolesSubject = new BehaviorSubject<RoleType[]>([])
  roles$ = this.userRoles.asObservable()

  // store the URL so we can redirect after logging in
  redirectUrl: string | null = null

  constructor(
      private readonly storageSvc: StorageService,
      private readonly userSvc: UserService,
      private readonly router: Router,
  ) {
  }


  login(request: LoginRequestModel): Observable<UserDetailModel> {
    if (this.storageSvc.isLoggedIn(request.login)) {
      return of(this.user)
    }
    return this.userSvc.login(request)
  }

  logout() {
    if (!this.user) return
    this.storageSvc.removeLoginUser(this.user)
    this.userSvc.logout(this.user.id).subscribe({
      next: (value) => {
        this.isUserLoggedIn.next(false)
      },
      complete: () => {
        this.isLoading = false
      },
    })
  }

  get isLoggedIn() {
    this.user = this.getLoginUser()
    if (this.user) {
      if (this.storageSvc.isLoggedIn(this.user.login)) {
        this.isUserLoggedIn.next(true)
      } else {
        this.isUserLoggedIn.next(false)
      }
    }
    return this.isUserLoggedIn.asObservable()
  }

  get roles() {
    this.user = this.getLoginUser()
    if (this.user) {
      this.userRoles.next(this.user.roles)
    }
    return this.userRoles.asObservable()
  }

  saveLoginUser(user: UserDetailModel) {
    this.storageSvc.saveLoginUser(user)
  }

  getLoginUser(): UserDetailModel {
    return this.storageSvc.getLoginUser()
  }

  getLoginRoles(): string[] {
    this.roleStrings = this.storageSvc.getLoginRoles()
    return this.roleStrings
  }

  // TODO better handling of role change
  setRoles(roles: RoleType[]): void {
    this.rolesSubject.next(roles)
  }

  hasOneOfRoles1(requiredRoles: RoleType[]): boolean {
    const currentRoles = this.rolesSubject.getValue()
    return requiredRoles.some((role) => currentRoles.includes(role))
  }

  hasOneOfRoles(roles: RoleType[]): boolean {
    return this.storageSvc.hasOneOfRoles(roles)
  }

  redirectUser() {
    const navigationExtras: NavigationExtras = {
      queryParamsHandling: 'preserve',
      preserveFragment: true,
    }
    const url = this.getRedirectUrl()
    this.router.navigate([url], navigationExtras)
  }

  private hasRole(role: RoleType): boolean {
    return this.roleStrings.includes(role.toString())
  }

  /**
   * Gets the redirect URL based on the user's roles. Highest permissions
   * come first (s. Redirects constant)
   * @private
   * @returns {string} The redirect URL.
   */
  private getRedirectUrl() {
    let url = '/login'
    this.getLoginRoles()

    for (let roleUrl of Redirects) {
      if (this.hasRole(roleUrl.role)) {
        url = roleUrl.url
        break
      }
    }
    return url
  }

  /*
   getToken() {
   return 'some-tokenl'
   }
   */
}
